import { FC, FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { accountErrorText } from '../core/errors'
import { PasswordPromptRequest } from '../core/_models'


/**
 * Every change to the account is confirmed with the current password. One prompt for all of them:
 * `ask()` opens it, and it stays open — with the reason — until the action goes through.
 */
export const usePasswordPrompt = () => {
  const [request, setRequest] = useState<PasswordPromptRequest | null>(null)

  const ask = useCallback((next: PasswordPromptRequest) => setRequest(next), [])
  const prompt = request ? <CurrentPasswordModal request={request} onClose={() => setRequest(null)} /> : null

  return { ask, prompt }
}

const CurrentPasswordModal: FC<{ request: PasswordPromptRequest; onClose: () => void }> = ({ request, onClose }) => {
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  // the field is disabled while the request runs and loses the cursor: give it back for the retry
  useEffect(() => {
    if (!loading && error) {
      input.current?.focus()
    }
  }, [loading, error])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (loading || password === '') {
      return
    }

    setLoading(true)
    setError(null)
    try {
      await request.action(password)
      onClose()
    } catch (e) {
      setPassword('')
      setError(accountErrorText(e))
      setLoading(false)
    }
  }

  return (
    <Modal show onHide={loading ? undefined : onClose} centered>
      <form onSubmit={submit} noValidate>
        <Modal.Header closeButton={!loading}>
          <Modal.Title>{request.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {request.warning && <div className='alert alert-warning'>{request.warning}</div>}
          <label className='form-label fw-bold' htmlFor='account_current_password'>
            Текущий пароль
          </label>
          <input
            id='account_current_password'
            ref={input}
            type='password'
            autoComplete='current-password'
            autoFocus
            className='form-control'
            value={password}
            disabled={loading}
            onChange={(event) => setPassword(event.target.value)}
          />
          {error && <div className='text-danger mt-2'>{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={loading}>
            Отмена
          </button>
          <button type='submit' className='btn btn-primary' disabled={loading || password === ''}>
            {loading ? (
              <>
                Проверяем... <span className='spinner-border spinner-border-sm align-middle ms-2' />
              </>
            ) : (
              'Продолжить'
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
